/** Collapsed attachments rail matches Tailwind `w-11`. */
export const SALES_ORDER_PREVIEW_SIDEBAR_COLLAPSED_PX = 44;

/** Matches the previous open width: `xl:w-[48vw] 2xl:w-[52rem]`. */
export const SALES_ORDER_PREVIEW_SIDEBAR_DEFAULT_VW = 0.48;
export const SALES_ORDER_PREVIEW_SIDEBAR_TWXL_MIN_PX = 1536;
export const SALES_ORDER_PREVIEW_SIDEBAR_TWXL_CAP_PX = 52 * 16;
export const SALES_ORDER_PREVIEW_SIDEBAR_MIN_PX = 280;
export const SALES_ORDER_PREVIEW_SIDEBAR_COOKIE_NAME = "sgs-so-preview-sidebar-width";
export const SALES_ORDER_PREVIEW_SIDEBAR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export function defaultSalesOrderPreviewSidebarWidthPx(viewportWidth: number): number {
  const fromViewport = Math.round(viewportWidth * SALES_ORDER_PREVIEW_SIDEBAR_DEFAULT_VW);
  if (viewportWidth >= SALES_ORDER_PREVIEW_SIDEBAR_TWXL_MIN_PX) {
    return Math.min(fromViewport, SALES_ORDER_PREVIEW_SIDEBAR_TWXL_CAP_PX);
  }
  return fromViewport;
}

export function clampSalesOrderPreviewSidebarWidthPx(widthPx: number, viewportWidth: number): number {
  const rounded = Number.isFinite(widthPx) ? Math.round(widthPx) : SALES_ORDER_PREVIEW_SIDEBAR_MIN_PX;
  const maxByViewport = Math.round(viewportWidth * 0.7);
  const maxByForm = viewportWidth - 420;
  const upper = Math.max(
    SALES_ORDER_PREVIEW_SIDEBAR_MIN_PX,
    Math.min(maxByViewport, maxByForm),
  );
  return Math.min(upper, Math.max(SALES_ORDER_PREVIEW_SIDEBAR_MIN_PX, rounded));
}

export function parseSalesOrderPreviewSidebarWidthCookie(cookieHeader: string): number | null {
  const prefix = `${SALES_ORDER_PREVIEW_SIDEBAR_COOKIE_NAME}=`;
  for (const part of cookieHeader.split(";")) {
    const trimmed = part.trim();
    if (!trimmed.startsWith(prefix)) {
      continue;
    }
    const parsed = Number(trimmed.slice(prefix.length));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    return Math.round(parsed);
  }
  return null;
}

export function salesOrderPreviewSidebarWidthCookieValue(widthPx: number): string {
  return `${SALES_ORDER_PREVIEW_SIDEBAR_COOKIE_NAME}=${Math.round(widthPx)}; Path=/; Max-Age=${SALES_ORDER_PREVIEW_SIDEBAR_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

export function readSalesOrderPreviewSidebarWidthPx(viewportWidth: number, cookieHeader: string): number {
  const stored = parseSalesOrderPreviewSidebarWidthCookie(cookieHeader);
  if (stored == null) {
    return defaultSalesOrderPreviewSidebarWidthPx(viewportWidth);
  }
  return clampSalesOrderPreviewSidebarWidthPx(stored, viewportWidth);
}

export function writeSalesOrderPreviewSidebarWidthCookie(
  widthPx: number,
  cookieTarget: { cookie: string },
): void {
  cookieTarget.cookie = salesOrderPreviewSidebarWidthCookieValue(widthPx);
}

export function initialSalesOrderPreviewSidebarWidthPx(): number {
  if (typeof window === "undefined") {
    return SALES_ORDER_PREVIEW_SIDEBAR_TWXL_CAP_PX;
  }
  return readSalesOrderPreviewSidebarWidthPx(window.innerWidth, window.document.cookie);
}
